import FrontdeskButton from '$components/FrontdeskButton';
import {Button, TextField} from '@mui/material';
import RejectAppointmentForm from './RejectAppointmentForm';
import {
  REJECT_APPOINTMENT_TITLE,
  REJECT_APPOINTMENT_TITLE_DESCRIPTION,
  OTHER_PERSONAL_REASON,
} from './constant';
import {SetStateAction, useCallback, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useRejectAppointmentHook} from '../../hooks/useRejectedAppointmentHook';
import {ICancellationReason} from '../../types';
import CheckBox from '$components/CheckBox';
import './styles.scss';

interface RejectAppointmentComponentProps {
  rejectAppointmentModal: boolean;
  setRejectAppointmentModal: React.Dispatch<SetStateAction<boolean>>;
  paymentId?: number;
  paymentStatus?: string;
}

const RejectAppointmentComponent: React.FC<RejectAppointmentComponentProps> = ({
  rejectAppointmentModal,
  setRejectAppointmentModal,
  paymentId,
  paymentStatus,
}) => {
  const params = useParams();
  const appointmentId = Number(params.id);
  const [cancellationReasonId, setCancellationReasonId] = useState<string>('');
  const [otherCancellationReason, setOtherCancellationReason] =
    useState<string>('');
  const [cancellationReasons, setCancellationReasons] = useState<
    ICancellationReason[]
  >([]);
  const [informPatient, setInformPatient] = useState<boolean>(false);
  const [refundPatient, setRefundPatient] = useState<boolean>(() => {
    return Boolean(paymentId && paymentStatus === 'paid');
  });
  const {isPending, sendRejectAppointment} = useRejectAppointmentHook(
    setRejectAppointmentModal
  );

  const isOtherPersonalCancellationReason = useCallback(
    (value: string) => {
      return cancellationReasons.some(
        reason =>
          reason.id.toString() === value &&
          reason.name === OTHER_PERSONAL_REASON
      );
    },
    [cancellationReasons]
  );

  const handleCancellationReasonChange = useCallback(
    (value: string) => {
      setCancellationReasonId(value);
      if (!isOtherPersonalCancellationReason(value)) {
        setOtherCancellationReason('');
      }
    },
    [isOtherPersonalCancellationReason]
  );

  const handleDeleteAppointmentClick = useCallback(() => {
    if (
      isOtherPersonalCancellationReason(cancellationReasonId) &&
      !otherCancellationReason?.trim()
    ) {
      return;
    }

    const payload = {
      cancellationReason: +cancellationReasonId,
      otherCancellationReason: isOtherPersonalCancellationReason(
        cancellationReasonId
      )
        ? otherCancellationReason
        : '',
      appointmentId: appointmentId,
      informPatient: informPatient,
      refundPatient: refundPatient,
      payment_id: paymentId,
      status: 'rejected',
    };
    sendRejectAppointment(payload);
  }, [
    cancellationReasonId,
    otherCancellationReason,
    appointmentId,
    informPatient,
    refundPatient,
    paymentId,
    isOtherPersonalCancellationReason,
    sendRejectAppointment,
  ]);

  return (
    <div className="cancel-appointment-container">
      <div className="cancel-appointment-title">
        <div className="reject-title-text">{REJECT_APPOINTMENT_TITLE}</div>
        <div className="title-desc">{REJECT_APPOINTMENT_TITLE_DESCRIPTION}</div>
      </div>
      <div className="cancel-appointment-body">
        <RejectAppointmentForm
          handleCancellationReasonChange={handleCancellationReasonChange}
          cancellationReasonId={cancellationReasonId}
          setCancellationReasons={setCancellationReasons}
        />
        {isOtherPersonalCancellationReason(cancellationReasonId) && (
          <div className="other-reason-input-container">
            <TextField
              className="other-reason-input"
              label="Please specify reason here"
              variant="outlined"
              multiline
              fullWidth
              value={otherCancellationReason}
              onChange={e => setOtherCancellationReason(e.target.value)}
            />
          </div>
        )}
        <div className="cancel-check-box">
          <CheckBox
            label="Inform Patient"
            onClick={() => setInformPatient(prevState => !prevState)}
          />
        </div>
        {paymentId && paymentStatus === 'paid' && (
          <div className="cancel-check-box-refund">
            <CheckBox
              label="Refund Patient"
              checked={refundPatient}
              onClick={() => setRefundPatient(prevState => !prevState)}
            />
          </div>
        )}
      </div>
      <div
        className={`cancel-appointment-footer ${
          isOtherPersonalCancellationReason(cancellationReasonId)
            ? 'with-textfield'
            : ''
        }`}
      >
        <Button
          className="cancel-btn"
          onClick={() => setRejectAppointmentModal(!rejectAppointmentModal)}
        >
          Cancel
        </Button>
        <FrontdeskButton
          title="Reject request"
          loading={isPending}
          disabled={
            !cancellationReasonId ||
            (isOtherPersonalCancellationReason(cancellationReasonId) &&
              !otherCancellationReason?.trim())
          }
          onClick={handleDeleteAppointmentClick}
        />
      </div>
    </div>
  );
};

export default RejectAppointmentComponent;
